import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hydrateCloverState } from '../redux/slices/cloverSlice';
import type { RootState } from '../redux/store';

/**
 * Hook to sync Clover state with localStorage
 * - Hydrates Redux state from localStorage on mount
 * - Saves Clover state to localStorage whenever it changes
 * 
 * This ensures the Clover integration persists across page refreshes
 */
export const useLocalStorageSync = () => {
  const dispatch = useDispatch();
  const cloverState = useSelector((state: RootState) => state.clover);

  // Hydrate from localStorage on mount
  useEffect(() => {
    const merchant_id = localStorage.getItem('clover_merchant_id');
    const employee_id = localStorage.getItem('clover_employee_id');
    const client_id = localStorage.getItem('clover_client_id');
    const code = localStorage.getItem('clover_code');
    const restaurantId = localStorage.getItem('restaurantId');

    // Only hydrate if we have data in localStorage
    if (merchant_id || employee_id || client_id || code || restaurantId) {
      dispatch(hydrateCloverState({
        merchant_id,
        employee_id,
        client_id,
        code,
        restaurantId,
      }));
    }
  }, [dispatch]);

  // Save to localStorage whenever clover state changes
  useEffect(() => {
    if (cloverState.merchant_id) {
      localStorage.setItem('clover_merchant_id', cloverState.merchant_id);
    }
    if (cloverState.employee_id) {
      localStorage.setItem('clover_employee_id', cloverState.employee_id);
    }
    if (cloverState.client_id) {
      localStorage.setItem('clover_client_id', cloverState.client_id);
    }
    if (cloverState.code) {
      localStorage.setItem('clover_code', cloverState.code);
    }
    // Note: restaurantId is managed by MainLayout from URL params
    // We only save it here during Clover integration flow
    // MainLayout will override this with the URL restaurantId
    if (cloverState.restaurantId) {
      const currentStoredId = localStorage.getItem('restaurantId');
      // Only update if not already set, to avoid conflicts with URL-based restaurantId
      if (!currentStoredId) {
        localStorage.setItem('restaurantId', cloverState.restaurantId);
      }
    }
  }, [
    cloverState.merchant_id,
    cloverState.employee_id,
    cloverState.client_id,
    cloverState.code,
    cloverState.restaurantId,
  ]);
};
